import { useState } from "react";

import type { CartSpace, Space } from "@prisma/client";
import { Link, useActionData } from "@remix-run/react";

import { Button, CountDownTimer, DialogHeader, DialogTitle, Modal } from "~/components";
import { Form, FormError, useForm, z } from "~/components/form";
import { ShareSpace } from "~/components/shared/ShareSpace";
import { SpaceImage, SpaceLocation } from "~/components/shared/Space";
import { DeleteIcon, ShareIcon } from "~/icons";
import { currency } from "~/lib/utils";

export const DELETE_CART_SPACE = "delete-cart-space";

const removeSpaceSchema = z.object({
    cartSpaceId: z.string(),
    intent: z.literal(DELETE_CART_SPACE),
});

export function SpaceDisplay({
    cartSpace,
    baseUrl,
    cart,
}: {
    cartSpace: CartSpace & { space: Space };
    baseUrl: string;
}) {
    const { space } = cartSpace;

    const [isOpen, setIsOpen] = useState(false);

    const section = space.section?.length === 1 ? `Section ${space.section}` : space.section;

    return (
        <div className="flex flex-col md:flex-row items-start gap-8">
            <SpaceImage className="w-48" src={space.thumbnail} />
            <section className="flex flex-col flex-1 gap-4">
                <section className="flex flex-col flex-1 justify-between md:flex-row md:items-center">
                    <Link to={`/map?spaceId=${cartSpace.space.lotSellUnitId}`}>
                        <h2 className="capitalize text-2xl font-title hover:underline">{section}</h2>
                    </Link>
                    <p className="capitalize text-xl">{currency(space.price)}</p>
                </section>

                <p className="opacity-60">
                    <SpaceLocation {...space} />
                </p>
                <CountDownTimer space={space} cart={cart} />
                <section className="flex items-center gap-6">
                    <Button variant="action" onClick={() => setIsOpen(true)} className="capitalize">
                        <ShareIcon className="w-4 h-4" />
                        Share
                    </Button>
                    <Modal open={isOpen} onOpenChange={() => setIsOpen(false)} triggerButton={false}>
                        <DialogHeader>
                            <DialogTitle>Share this memorial space</DialogTitle>
                        </DialogHeader>
                        <ShareSpace baseUrl={baseUrl} space={space} />
                    </Modal>
                    <DeleteSpace cartSpaceId={cartSpace.id} />
                </section>
            </section>
        </div>
    );
}

function DeleteSpace({ cartSpaceId }: { cartSpaceId: string }) {
    const lastResult = useActionData();
    const [form] = useForm(removeSpaceSchema, {
        lastResult,
        defaultValue: {
            cartSpaceId,
        },
    });
    return (
        <Form method="post" form={form}>
            <FormError />
            <input name="cartSpaceId" value={cartSpaceId} type="hidden" readOnly />
            <Button
                className="flex items-center justify-center gap-1 text-destructive text-sm capitalize"
                name="intent"
                value={DELETE_CART_SPACE}
                type="submit"
                variant="action"
            >
                <DeleteIcon className="w-4 h-4" />
                Remove
            </Button>
        </Form>
    );
}
